/**
 * Hook to persist epic expand/collapse state in localStorage
 * Story 258: Navigate epics more easily
 *
 * Stores collapsed epic slugs (expanded is default state).
 * Per-project storage key prevents cross-project interference.
 */

import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY_PREFIX = 'epic-expand-';

function getStorageKey(projectSlug: string): string {
  return `${STORAGE_KEY_PREFIX}${projectSlug}`;
}

function loadCollapsedSlugs(projectSlug: string): Set<string> {
  try {
    const stored = localStorage.getItem(getStorageKey(projectSlug));
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    }
  } catch {
    // Ignore parse errors, return empty set
  }
  return new Set();
}

function saveCollapsedSlugs(projectSlug: string, slugs: Set<string>): void {
  try {
    localStorage.setItem(getStorageKey(projectSlug), JSON.stringify([...slugs]));
  } catch {
    // Ignore storage errors (quota, private mode)
  }
}

interface EpicExpandState {
  isExpanded: (epicSlug: string) => boolean;
  toggleExpanded: (epicSlug: string) => void;
  setExpanded: (epicSlug: string, expanded: boolean) => void;
}

/**
 * Manages epic expand/collapse state with localStorage persistence
 * @param projectSlug - Project identifier for storage key
 * @returns Object with isExpanded check and toggle/set functions
 */
export function useEpicExpandState(projectSlug: string): EpicExpandState {
  const [collapsedSlugs, setCollapsedSlugs] = useState<Set<string>>(() =>
    loadCollapsedSlugs(projectSlug)
  );

  // Reload when project changes
  useEffect(() => {
    setCollapsedSlugs(loadCollapsedSlugs(projectSlug));
  }, [projectSlug]);

  // Persist on change
  useEffect(() => {
    saveCollapsedSlugs(projectSlug, collapsedSlugs);
  }, [projectSlug, collapsedSlugs]);

  const isExpanded = useCallback(
    (epicSlug: string): boolean => !collapsedSlugs.has(epicSlug),
    [collapsedSlugs]
  );

  const toggleExpanded = useCallback((epicSlug: string) => {
    setCollapsedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(epicSlug)) {
        next.delete(epicSlug);
      } else {
        next.add(epicSlug);
      }
      return next;
    });
  }, []);

  const setExpanded = useCallback((epicSlug: string, expanded: boolean) => {
    setCollapsedSlugs((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.delete(epicSlug);
      } else {
        next.add(epicSlug);
      }
      return next;
    });
  }, []);

  return { isExpanded, toggleExpanded, setExpanded };
}
